/** A picked file → rows of text cells, on this device. The Worker takes `{ filename, entity, rows }` and parses nothing else. */
import { parseCsv } from "@helmr/importer";

export const MAX_SHEET_BYTES = 5 * 1024 * 1024;
export const MAX_SHEET_ROWS = 20_000;

export class SheetError extends Error {}

const kind = (name: string) => (/\.(xlsx|xlsm|xls)$/i.test(name) ? "excel" : /\.(csv|txt|tsv)$/i.test(name) ? "csv" : "other");

export async function readSheet(file: File): Promise<string[][]> {
  if (kind(file.name) === "other") throw new SheetError("Use a CSV or Excel file (.csv, .xlsx).");
  if (file.size > MAX_SHEET_BYTES) throw new SheetError(`This file is ${(file.size / 1048576).toFixed(1)} MB. A sheet can be at most ${MAX_SHEET_BYTES / 1048576} MB; split it and upload the parts.`);
  let rows: string[][];
  try {
    if (kind(file.name) === "csv") {
      rows = parseCsv(new TextDecoder("utf-8").decode(await file.arrayBuffer()));
    } else {
      const XLSX = await import("xlsx");
      const wb = XLSX.read(new Uint8Array(await file.arrayBuffer()), { type: "array", cellDates: false });
      const first = wb.Sheets[wb.SheetNames[0] ?? ""];
      if (!first) throw new SheetError("This workbook has no sheets.");
      // `raw: false` gives the text the sheet shows (dates and numbers as typed), which is what the importer expects.
      rows = (XLSX.utils.sheet_to_json(first, { header: 1, raw: false, defval: "", blankrows: false }) as unknown[][]).map((r) => r.map((c) => String(c ?? "")));
    }
  } catch (e) {
    if (e instanceof SheetError) throw e;
    throw new SheetError("This file could not be read. Save it as CSV or .xlsx and try again.");
  }
  if (rows.length === 0 || rows.every((r) => r.every((c) => c.trim() === ""))) throw new SheetError("This sheet is empty.");
  if (rows.length - 1 > MAX_SHEET_ROWS) throw new SheetError(`This sheet has ${rows.length - 1} data rows. A sheet can have at most ${MAX_SHEET_ROWS}; split it and upload the parts.`);
  return rows;
}
